/**
 * Barber Station Indicator
 *
 * Shows which barber is currently signed in at this station.
 * Click to switch barber or sign out of station.
 * Long-press/right-click to lock screen.
 *
 * Note: This is STATION-level auth, not Firebase auth.
 * - Firebase auth = shop admin login
 * - Station auth = which barber is at this iPad
 */

import React, { useState } from 'react';
import { Lock, User } from 'lucide-react';
import { usePinLock } from '../../contexts/PinLockContext';
import ComprehensiveBarberProfile from '../ComprehensiveBarberProfile';

// The station/operator chip (+ its Lock Screen) is part of the OPERATOR PIN MODEL — live on STAGING,
// OFF on prod until the security one-shot. Gate the chip to the model so prod is UNIFORMLY "logout
// only": the top bar looked inconsistent (chip on the web browser where a station operator was once
// set, none on the native app) because the model is only half-present on prod. Ships consistently to
// every device with the one-shot. (Jose 2026-09-24: "I just want the consistency.")
const OPERATOR_MODEL = true; // operator identity/lock indicator enforced everywhere (prod one-shot 2026-09-26)

interface BarberStationIndicatorProps {
  className?: string;
}

const BarberStationIndicator: React.FC<BarberStationIndicatorProps> = ({ className = '' }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { currentBarber, lock } = usePinLock();

  const handleLongPress = () => {
    // Long press to lock screen
    if (currentBarber?.hasPin) {
      lock();
    }
  };

  // Off on prod (operator model gated) → no chip anywhere = consistent with the native app.
  // On staging (model on) → show whenever an operator is signed in.
  if (!OPERATOR_MODEL || !currentBarber) {
    return null;
  }

  // Get initials
  const initials = currentBarber.barberName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        onContextMenu={(e) => {
          e.preventDefault();
          handleLongPress();
        }}
        className={`flex items-center gap-2 px-3 py-2 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-lg transition-colors border border-purple-200 dark:border-purple-700 touch-manipulation ${className}`}
        title={currentBarber.hasPin ? 'Right-click to lock' : ''}
      >
        {/* Avatar */}
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
          {initials}
        </div>

        {/* Name */}
        <span className="text-sm font-medium text-purple-700 dark:text-purple-300 max-w-[100px] truncate">
          {currentBarber.barberName.split(' ')[0]}
        </span>

        {/* Lock indicator */}
        {currentBarber.hasPin && (
          <Lock className="h-3.5 w-3.5 text-purple-500" />
        )}
      </button>

      {/* Dropdown Menu */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 py-2 min-w-[200px]">
            {/* Current User Info */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <p className="font-semibold text-gray-900 dark:text-white">
                {currentBarber.barberName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Signed in at this station
              </p>
            </div>

            {/* My Profile — the barber's own numbers (Overview + Finance). Lives here on the operator
                chip so it's always one tap from anywhere (Jose 2026-09-27). */}
            <button
              onClick={() => {
                setShowMenu(false);
                setShowProfile(true);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <User className="h-5 w-5 text-purple-500" />
              <span className="font-medium text-gray-800 dark:text-gray-200">My Profile</span>
            </button>

            {/* Lock Screen — the barber hand-off action. On the shared shop account this returns to
                the operator PIN pad so the next person taps in (Jose 2026-09-24: barbers LOCK, they
                don't sign out the shop account — that's owner-only in the top bar). */}
            <button
              onClick={() => {
                setShowMenu(false);
                lock();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <Lock className="h-5 w-5 text-purple-500" />
              <span className="font-medium text-gray-800 dark:text-gray-200">Lock Screen</span>
            </button>
          </div>
        </>
      )}

      {/* My Profile modal — opens the operator's own ComprehensiveBarberProfile (gated to Overview +
          Finance for a barber; the component gates its own tabs by role). */}
      {showProfile && currentBarber?.barberId && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-stretch sm:items-center sm:justify-center sm:p-4"
          onClick={() => setShowProfile(false)}
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div
            className="bg-gray-50 dark:bg-gray-900 w-full sm:max-w-4xl h-full sm:h-[90vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 h-12 border-b border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-800">
              <span className="font-semibold text-gray-900 dark:text-white">My Profile</span>
              <button onClick={() => setShowProfile(false)} className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">✕ Close</button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <ComprehensiveBarberProfile initialBarberId={currentBarber.barberId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarberStationIndicator;
